import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Clock, Pencil, Music, Users, Calendar, Sparkles, Heart, TrendingUp, Trash2 } from 'lucide-react';
import { Song } from '@/types/sequence';
import AddSongForm from './AddSongForm';

interface SongsTabProps {
  userSongs: Record<number, Song[]>;
  displayId: string;
  onSongAdded: (song: Song) => void;
  onSongDeleted?: (songId: string) => void;
}

const SongsTab: React.FC<SongsTabProps> = ({ userSongs, displayId, onSongAdded, onSongDeleted }) => {
  const [isAddSongOpen, setIsAddSongOpen] = useState(false);
  const [editingSong, setEditingSong] = useState<Song | null>(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [songToDelete, setSongToDelete] = useState<Song | null>(null);
  const years = Object.keys(userSongs).sort((a, b) => Number(b) - Number(a));

  const handleAddSong = (song: Song) => {
    onSongAdded(song);
  };

  const handleEditClick = (song: Song) => {
    setEditingSong(song);
    setIsAddSongOpen(true);
  };

  const handleDeleteClick = (song: Song) => {
    setSongToDelete(song);
    setDeleteConfirmOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!songToDelete || !onSongDeleted) return;
    
    try {
      // Call the parent's delete handler
      onSongDeleted(songToDelete.id);
      
      // Close the modal
      setDeleteConfirmOpen(false);
      setSongToDelete(null);
    } catch (error) {
      console.error('Error deleting song:', error);
      // You could add a toast notification here for error handling
    }
  };

  const handleCancelDelete = () => {
    setDeleteConfirmOpen(false);
    setSongToDelete(null);
  };

  // Check if user has a valid display (but don't block functionality)
  const hasDisplay = displayId && displayId.trim() !== '';

  const totalSongs = Object.values(userSongs).flat().length;

  return (
    <div className="space-y-6">
      {/* Explanatory Header */}
      <Card className="border-blue-200 bg-linear-to-r from-blue-50 to-indigo-50">
        <CardHeader className="pb-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <Music className="h-5 w-5 text-blue-600" />
            </div>
            <div>
              <CardTitle className="text-lg text-blue-900">Track Your Holiday Music Journey</CardTitle>
              <p className="text-sm text-blue-700 mt-1">
                Build your personal collection and help the community discover amazing songs
              </p>
            </div>
          </div>
        </CardHeader>
        <CardContent className="pt-0">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="flex items-center gap-3 p-3 bg-white rounded-lg border border-blue-100">
              <Heart className="h-4 w-4 text-red-500" />
              <div>
                <p className="font-medium text-sm text-gray-900">For You</p>
                <p className="text-xs text-gray-600">Keep track of songs you've used over the years</p>
              </div>
            </div>
            <div className="flex items-center gap-3 p-3 bg-white rounded-lg border border-blue-100">
              <Users className="h-4 w-4 text-green-500" />
              <div>
                <p className="font-medium text-sm text-gray-900">For Community</p>
                <p className="text-xs text-gray-600">Help others discover popular holiday music</p>
              </div>
            </div>
            <div className="flex items-center gap-3 p-3 bg-white rounded-lg border border-blue-100">
              <TrendingUp className="h-4 w-4 text-purple-500" />
              <div>
                <p className="font-medium text-sm text-gray-900">Discover Trends</p>
                <p className="text-xs text-gray-600">See what songs are trending each year</p>
              </div>
            </div>
          </div>
          <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
            <div className="flex items-start gap-2">
              <Sparkles className="h-4 w-4 text-amber-600 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-amber-800">
                  {!hasDisplay ? "Start building your collection!" : "Track your display songs!"}
                </p>
                <p className="text-xs text-amber-700 mt-1">
                  {!hasDisplay 
                    ? "You can add songs to your collection even before creating a display. When you're ready to create a display, you can easily associate your saved songs with it."
                    : "Add songs you've used in your display to help other enthusiasts discover what works well and track your own music journey over the years."
                  }
                </p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Songs Section */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <CardTitle className="flex items-center gap-2">
                <Music className="h-5 w-5" />
                My Songs ({totalSongs})
              </CardTitle>
            </div>
          <div className="flex justify-end">
            <Button onClick={() => {
              setEditingSong(null);
              setIsAddSongOpen(true);
            }}>
              Add New Song
            </Button>
          </div>
          </div>
              </CardHeader>
              <CardContent>
          {years.length === 0 ? (
            <div className="text-center py-12 space-y-4">
              <div className="mx-auto w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center">
                <Music className="h-6 w-6 text-gray-400" />
              </div>
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">No songs added yet</h3>
                <p className="text-gray-500 mb-6 max-w-md mx-auto">
                  Start building your holiday music collection! Add songs you love, have used in displays, or want to remember for future years.
                </p>
                <Button onClick={() => {
                  setEditingSong(null);
                  setIsAddSongOpen(true);
                }}>Add Your First Song</Button>
              </div>
            </div>
          ) : (
            <div className="space-y-6">
              {years.map(year => (
                <div key={year} className="border rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-4">
                    <Calendar className="h-4 w-4 text-blue-600" />
                    <h3 className="font-semibold text-lg">{year}</h3>
                    <Badge variant="outline">{(userSongs[Number(year)] || []).length} songs</Badge>
                  </div>
                  <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                          <TableHead>Song</TableHead>
                      <TableHead>Artist</TableHead>
                          <TableHead>Duration</TableHead>
                      <TableHead>Genre</TableHead>
                          <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                        {(userSongs[Number(year)] || []).map((song) => (
                          <TableRow key={song.id} className="group">
                            <TableCell>
                          <div className="flex items-center gap-3">
                                {song.album_cover_url ? (
                                  <img 
                                    src={song.album_cover_url} 
                                    alt={`${song.title} cover`}
                                    className="w-10 h-10 rounded object-cover border border-gray-200"
                                    onError={(e) => {
                                      // Fallback to placeholder if image fails to load
                                      e.currentTarget.style.display = 'none';
                                      e.currentTarget.nextElementSibling?.classList.remove('hidden');
                                    }}
                                  />
                            ) : null}
                                {!song.album_cover_url && (
                                  <div className="w-10 h-10 bg-gray-100 rounded flex items-center justify-center border border-gray-200">
                                    <Music className="h-4 w-4 text-gray-400" />
                              </div>
                                )}
                                {/* Hidden fallback div for when image fails to load */}
                                <div className="w-10 h-10 bg-gray-100 rounded flex items-center justify-center border border-gray-200 hidden">
                                  <Music className="h-4 w-4 text-gray-400" />
                                </div>
                                <div>
                                  <p className="font-medium">{song.title}</p>
                                  {song.reused_from && (
                                    <p className="text-xs text-gray-500">Reused from previous year</p>
                                  )}
                                </div>
                          </div>
                        </TableCell>
                        <TableCell>{song.artist}</TableCell>
                            <TableCell>
                              {song.duration && (
                                <div className="flex items-center gap-1">
                                  <Clock className="h-3 w-3 text-gray-400" />
                                  <span className="text-sm">{song.duration}</span>
                                </div>
                              )}
                            </TableCell>
                        <TableCell>
                          {song.genre && (
                                <Badge variant="secondary" className="text-xs">
                                  {song.genre}
                                </Badge>
                          )}
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleEditClick(song)}
                              className="opacity-0 group-hover:opacity-100 transition-opacity"
                            >
                              <Pencil className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDeleteClick(song)}
                              className="opacity-0 group-hover:opacity-100 transition-opacity text-red-600 hover:text-red-700 hover:bg-red-50"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                  </div>
                </div>
          ))}
        </div>
          )}
        </CardContent>
      </Card>

      {/* Delete Confirmation Modal */}
      {deleteConfirmOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 bg-red-100 rounded-full">
                <Trash2 className="h-5 w-5 text-red-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900">Delete Song</h3>
            </div>
            
            <p className="text-gray-600 mb-6">
              Are you sure you want to delete "{songToDelete?.title}" by {songToDelete?.artist}? 
              This action cannot be undone.
            </p>
            
            <div className="flex justify-end gap-3">
              <Button
                variant="outline"
                onClick={handleCancelDelete}
              >
                Cancel
              </Button>
              <Button
                variant="destructive"
                onClick={handleConfirmDelete}
                className="bg-red-600 hover:bg-red-700"
              >
                Delete Song
              </Button>
            </div>
          </div>
        </div>
      )}

      <AddSongForm
        isOpen={isAddSongOpen}
        onClose={() => {
          setIsAddSongOpen(false);
          setEditingSong(null);
        }}
        onSongAdded={handleAddSong}
        displayId={displayId}
        editingSong={editingSong}
      />
    </div>
  );
};

export default SongsTab;
